import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { FaEdit, FaTrash, FaPlus, FaExchangeAlt, FaPause, FaPlay, FaSpinner, FaClipboardList, FaDownload, FaUpload, FaFileExcel } from 'react-icons/fa';
import {
  Ruler, Cog, Gauge, Wrench, Shield, Zap, Wifi, Thermometer, Award,
  Link as LinkIcon, Layers, Truck, Scale, FileCheck, MapPin, Star,
  GraduationCap, Activity, Box, Anchor, HardHat, ChevronDown, ChevronRight
} from 'lucide-react';

// Section icons mapping for templates
const SECTION_ICONS = {
  Ruler, Cog, Gauge, Wrench, Shield, Zap, Wifi, Thermometer, Award,
  Link: LinkIcon, Layers, Truck, Scale, FileCheck, MapPin, Star,
  GraduationCap, Activity, Box, Anchor, HardHat
};

// Dynamic Field Component for template fields
const DynamicField = ({ field, value, onChange, language }) => {
  const label = language === 'fr' ? field.label_fr : (field.label_en || field.label_fr);
  const helpText = language === 'fr' ? field.help_text_fr : (field.help_text_en || field.help_text_fr);
  
  const renderInput = () => {
    switch (field.field_type) {
      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ''}
          />
        );
        
      case 'number':
        return (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={field.placeholder || ''}
              className="flex-1"
            />
            {field.unit && (
              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-2 rounded">{field.unit}</span>
            )}
          </div>
        );
        
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ''}
            className="w-full min-h-[80px] p-3 border rounded-md resize-y text-sm"
          />
        );
        
      case 'select':
        return (
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger>
              <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((opt, idx) => (
                <SelectItem key={idx} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
        
      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
        return (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[40px]">
              {selectedValues.map((v, idx) => (
                <Badge key={idx} variant="secondary" className="bg-[#635BFF]/10 text-[#635BFF]">
                  {v}
                  <button
                    type="button"
                    onClick={() => onChange(selectedValues.filter(sv => sv !== v))}
                    className="ml-1 hover:text-red-500"
                  >
                    ×
                  </button>
                </Badge>
              ))}
              {selectedValues.length === 0 && (
                <span className="text-gray-400 text-sm">{language === 'fr' ? 'Cliquez pour ajouter...' : 'Click to add...'}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {(field.options || []).filter(opt => !selectedValues.includes(opt)).map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => onChange([...selectedValues, opt])}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-[#635BFF]/10 hover:text-[#635BFF] rounded transition-colors"
                >
                  + {opt}
                </button>
              ))}
            </div>
          </div>
        );
        
      case 'boolean':
        return (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Switch
              checked={value === true || value === 'true'}
              onCheckedChange={(checked) => onChange(checked)}
            />
            <span className="text-sm">{value ? (language === 'fr' ? 'Oui' : 'Yes') : (language === 'fr' ? 'Non' : 'No')}</span>
          </div>
        );
        
      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
        
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ''}
          />
        );
    }
  };
  
  return (
    <div className={field.field_type === 'multiselect' || field.field_type === 'textarea' ? 'md:col-span-2' : ''}>
      <Label className="flex items-center gap-1 mb-2">
        {label}
        {field.required && <span className="text-red-500">*</span>}
        {field.unit && field.field_type !== 'number' && (
          <span className="text-xs text-gray-400">({field.unit})</span>
        )}
      </Label>
      {renderInput()}
      {helpText && (
        <p className="text-xs text-gray-500 mt-1">{helpText}</p>
      )}
    </div>
  );
};

export default function AdminProducts() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [editProduct, setEditProduct] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newOwnerId, setNewOwnerId] = useState('');
  const [activeTab, setActiveTab] = useState('general');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  
  // Template state for dynamic fields
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  
  const [formData, setFormData] = useState({
    name_fr: '',
    name_en: '',
    description_fr: '',
    description_en: '',
    category_id: '',
    shop_id: 'none',
    supplier_id: '',
    location: '',
    price_new: '',
    price_used: '',
    price_rental: '',
    show_price: true,
    stock_new: 0,
    stock_used: 0,
    stock_rental: 0,
    delivery_time: '',
    delivery_time_type: 'fixed',
    availability_status: 'available',
    images: [],
    featured: false,
    listing_type: '',
    brand: '',
    model: '',
    year: '',
    condition_details: '',
    weight: '',
    dimensions: '',
    technical_specs: '',
    warranty: '',
    // Dynamic template fields
    template_fields: {},
  });
  const [usersLoaded, setUsersLoaded] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchShops();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (user?.id) {
      fetchUsers();
    }
  }, [user]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?include_status=all`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch template when category changes
  const fetchTemplateForCategory = useCallback(async (categoryId) => {
    if (!categoryId) {
      setCurrentTemplate(null);
      return;
    }
    
    setLoadingTemplate(true);
    try {
      const response = await axios.get(`${API}/templates/by-category/${categoryId}`);
      const template = response.data;
      
      if (template && template.id !== 'default' && template.sections?.length > 0) {
        setCurrentTemplate(template);
        // Expand all sections by default
        const expanded = {};
        template.sections.forEach(s => { expanded[s.id] = true; });
        setExpandedSections(expanded);
      } else {
        setCurrentTemplate(null);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      setCurrentTemplate(null);
    } finally {
      setLoadingTemplate(false);
    }
  }, [API]);

  // Watch for category changes in the form
  useEffect(() => {
    if (dialogOpen && formData.category_id) {
      fetchTemplateForCategory(formData.category_id);
    }
  }, [formData.category_id, dialogOpen, fetchTemplateForCategory]);

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops`);
      setShops(response.data);
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/admin/all-users?admin_id=${user?.id}`);
      setUsers(response.data);
      setUsersLoaded(true);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const getOwnerName = (product) => {
    const ownerId = product.supplier_id || product.created_by_user_id;
    if (!ownerId) return 'Non assigné';
    if (!usersLoaded || users.length === 0) return 'Chargement...';
    const owner = users.find(u => u.id === ownerId);
    return owner ? owner.name : 'Utilisateur inconnu';
  };

  const openOwnerDialog = (product) => {
    setSelectedProduct(product);
    setNewOwnerId(product.supplier_id || product.created_by_user_id || '');
    setOwnerDialogOpen(true);
  };

  const handlePositionChange = async (productId, newPosition) => {
    try {
      await axios.patch(`${API}/products/${productId}/position?position=${newPosition}`);
      setProducts(prevProducts => 
        prevProducts.map(p => 
          p.id === productId ? { ...p, position: newPosition } : p
        ).sort((a, b) => (a.position || 999) - (b.position || 999))
      );
      toast.success(`Position mise à jour: ${newPosition}`);
    } catch (error) {
      console.error('Error updating position:', error);
      toast.error('Erreur lors de la mise à jour de la position');
    }
  };

  const handleChangeOwner = async () => {
    if (!selectedProduct || !newOwnerId) return;
    try {
      await axios.patch(`${API}/products/${selectedProduct.id}/owner?supplier_id=${newOwnerId}`);
      toast.success('Propriétaire modifié avec succès');
      setOwnerDialogOpen(false);
      setProducts(prevProducts => 
        prevProducts.map(p => 
          p.id === selectedProduct.id 
            ? { ...p, supplier_id: newOwnerId, created_by_user_id: newOwnerId }
            : p
        )
      );
      await fetchProducts();
    } catch (error) {
      console.error('Error changing owner:', error);
      toast.error('Erreur lors du changement de propriétaire');
    }
  };

  const handleToggleStatus = async (productId, currentStatus) => {
    setTogglingId(productId);
    const newStatus = (currentStatus === 'active' || currentStatus === 'approved') ? 'inactive' : 'active';
    try {
      await axios.patch(`${API}/products/${productId}/status?status=${newStatus}`);
      toast.success(newStatus === 'inactive' ? 'Produit désactivé' : 'Produit réactivé');
      fetchProducts();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Erreur lors du changement de statut');
    } finally {
      setTogglingId(null);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImages(true);
    const uploadedUrls = [];
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const response = await axios.post(`${API}/upload-image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const imageUrl = response.data.url || response.data.image_url;
        if (imageUrl) {
          uploadedUrls.push(imageUrl);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Erreur upload image');
      }
    }
    
    if (uploadedUrls.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
      toast.success(`${uploadedUrls.length} image(s) ajoutée(s)`);
    }
    
    setUploadingImages(false);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        shop_id: formData.shop_id === 'none' ? null : formData.shop_id,
        price_new: formData.price_new ? parseFloat(formData.price_new) : null,
        price_used: formData.price_used ? parseFloat(formData.price_used) : null,
        price_rental: formData.price_rental ? parseFloat(formData.price_rental) : null,
        stock_new: parseInt(formData.stock_new) || 0,
        stock_used: parseInt(formData.stock_used) || 0,
        stock_rental: parseInt(formData.stock_rental) || 0,
        status: 'active',
        created_by_user_id: user.id,
        // Dynamic template fields
        template_fields: formData.template_fields || {},
        template_id: currentTemplate?.id || null,
      };

      if (editProduct) {
        await axios.put(`${API}/products/${editProduct.id}`, data);
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Produit créé');
      }
      
      setDialogOpen(false);
      setEditProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setFormData({
      name_fr: product.name_fr || '',
      name_en: product.name_en || '',
      description_fr: product.description_fr || '',
      description_en: product.description_en || '',
      category_id: product.category_id || '',
      shop_id: product.shop_id || 'none',
      location: product.location || '',
      price_new: product.price_new || '',
      price_used: product.price_used || '',
      price_rental: product.price_rental || '',
      show_price: product.show_price !== undefined ? product.show_price : true,
      stock_new: product.stock_new || 0,
      stock_used: product.stock_used || 0,
      stock_rental: product.stock_rental || 0,
      availability_status: product.availability_status || 'available',
      delivery_time: product.delivery_time || '',
      delivery_time_type: product.delivery_time_type || 'fixed',
      images: product.images || [],
      featured: product.featured || false,
      listing_type: product.listing_type || '',
      brand: product.brand || '',
      model: product.model || '',
      year: product.year || '',
      condition_details: product.condition_details || '',
      weight: product.weight || '',
      dimensions: product.dimensions || '',
      technical_specs: product.technical_specs || '',
      warranty: product.warranty || '',
      // Dynamic template fields
      template_fields: product.template_fields || {},
    });
    setActiveTab('general');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      try {
        await axios.delete(`${API}/products/${id}`);
        toast.success('Produit supprimé');
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const resetForm = () => {
    setCurrentTemplate(null);
    setExpandedSections({});
    setFormData({
      name_fr: '',
      name_en: '',
      description_fr: '',
      description_en: '',
      category_id: '',
      shop_id: 'none',
      location: '',
      price_new: '',
      price_used: '',
      price_rental: '',
      show_price: true,
      stock_new: 0,
      stock_used: 0,
      stock_rental: 0,
      availability_status: 'available',
      delivery_time: '',
      delivery_time_type: 'fixed',
      images: [],
      featured: false,
      listing_type: '',
      brand: '',
      model: '',
      year: '',
      condition_details: '',
      weight: '',
      dimensions: '',
      technical_specs: '',
      warranty: '',
      template_fields: {},
    });
    setActiveTab('general');
  };

  const handleNewProduct = () => {
    setEditProduct(null);
    resetForm();
    setDialogOpen(true);
  };

  // Import/Export CSV functions
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);

  const handleExportCSV = async () => {
    try {
      window.open(`${API}/export/products`, '_blank');
      toast.success('Export CSV démarré');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/products/import/template`, '_blank');
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error('Veuillez sélectionner un fichier CSV');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/products/import?user_id=${user.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { message, errors } = response.data;
      toast.success(message);
      
      if (errors && errors.length > 0) {
        console.warn('Import errors:', errors);
        toast.warning(`${errors.length} erreur(s) lors de l'import`);
      }

      setImportDialogOpen(false);
      setImportFile(null);
      fetchProducts();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <AdminLayout>
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-products">
      <div className="p-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-[#0A2540]" data-testid="admin-products-title">
            {t('admin.products')} ({products.length})
          </h1>
          <div className="flex flex-wrap gap-2">
            {/* Import/Export Buttons */}
            <Button 
              variant="outline" 
              onClick={handleExportCSV}
              className="text-sm"
              data-testid="export-csv-btn"
            >
              <FaDownload className="mr-1" /> Exporter CSV
            </Button>
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="text-sm" data-testid="import-csv-btn">
                  <FaUpload className="mr-1" /> Importer CSV
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FaFileExcel className="text-green-600" /> Import CSV Produits
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 mb-2">
                      <strong>Format supporté :</strong> Fichier CSV avec colonnes standards + colonnes dynamiques (préfixe tf_)
                    </p>
                    <Button 
                      variant="link" 
                      onClick={handleDownloadTemplate}
                      className="text-blue-600 p-0 h-auto text-sm"
                    >
                      <FaDownload className="mr-1" /> Télécharger le modèle CSV
                    </Button>
                  </div>
                  
                  <div>
                    <Label>Fichier CSV</Label>
                    <Input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setImportFile(e.target.files[0])}
                      className="mt-2"
                    />
                    {importFile && (
                      <p className="text-sm text-gray-500 mt-1">
                        Fichier sélectionné : {importFile.name}
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => { setImportDialogOpen(false); setImportFile(null); }}
                    >
                      Annuler
                    </Button>
                    <Button 
                      onClick={handleImportCSV}
                      disabled={!importFile || importing}
                      className="bg-[#635BFF]"
                    >
                      {importing ? (
                        <><FaSpinner className="animate-spin mr-1" /> Import en cours...</>
                      ) : (
                        <><FaUpload className="mr-1" /> Importer</>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Product Button */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={handleNewProduct} className="bg-[#635BFF] text-sm" data-testid="add-product-btn">
                  <FaPlus className="mr-1" /> Ajouter
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl">
                  {editProduct ? '✏️ Modifier le produit' : '➕ Nouveau produit'}
                </DialogTitle>
              </DialogHeader>

              {/* Tabs Navigation */}
              <div className="flex flex-wrap gap-2 border-b pb-2 mb-4">
                {[
                  { id: 'general', label: '📝 Général' },
                  { id: 'pricing', label: '💰 Prix & Stock' },
                  { id: 'specs', label: '📋 Fiche technique', hasTemplate: true },
                  { id: 'details', label: '📋 Détails' },
                  { id: 'images', label: '🖼️ Images' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      activeTab === tab.id
                        ? 'bg-[#0A2540] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                    {tab.hasTemplate && currentTemplate && (
                      <span className="bg-[#635BFF] text-white text-xs px-1.5 py-0.5 rounded-full">
                        {currentTemplate.fields?.length || 0}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* TAB: GENERAL */}
                {activeTab === 'general' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="flex items-center gap-1">Nom (FR) <span className="text-red-500">*</span></Label>
                        <Input
                          value={formData.name_fr}
                          onChange={(e) => setFormData({ ...formData, name_fr: e.target.value })}
                          placeholder="Nom du produit en français"
                          required
                        />
                      </div>
                      <div>
                        <Label>Nom (EN)</Label>
                        <Input
                          value={formData.name_en}
                          onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                          placeholder="Product name in English"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Description (FR)</Label>
                        <textarea
                          className="w-full min-h-[100px] p-3 border rounded-md resize-y"
                          value={formData.description_fr}
                          onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                          placeholder="Description détaillée du produit..."
                        />
                      </div>
                      <div>
                        <Label>Description (EN)</Label>
                        <textarea
                          className="w-full min-h-[100px] p-3 border rounded-md resize-y"
                          value={formData.description_en}
                          onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                          placeholder="Description in English..."
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Catégorie</Label>
                        <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner une catégorie" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {i18n.language === 'fr' ? cat.name_fr : cat.name_en}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Boutique (Optionnel)</Label>
                        <Select value={formData.shop_id || "none"} onValueChange={(value) => setFormData({ ...formData, shop_id: value === "none" ? "" : value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Pas de boutique" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Pas de boutique</SelectItem>
                            {shops.map(shop => (
                              <SelectItem key={shop.id} value={shop.id}>{shop.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Localisation</Label>
                        <Input
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          placeholder="ex: Paris, France"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Marque</Label>
                        <Input
                          value={formData.brand}
                          onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                          placeholder="ex: Caterpillar, Soilmec..."
                        />
                      </div>
                      <div>
                        <Label>Modèle</Label>
                        <Input
                          value={formData.model}
                          onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                          placeholder="ex: SR-75, B180..."
                        />
                      </div>
                      <div>
                        <Label>Année</Label>
                        <Input
                          type="number"
                          value={formData.year}
                          onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                          placeholder="ex: 2022"
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 p-4 bg-[#635BFF]/5 rounded-lg border border-[#635BFF]/30">
                      <Checkbox
                        checked={formData.featured}
                        onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                      />
                      <Label className="cursor-pointer font-medium text-[#5248d9]">
                        ⭐ Produit mis en avant (Featured)
                      </Label>
                    </div>
                  </div>
                )}

                {/* TAB: PRICING & STOCK */}
                {activeTab === 'pricing' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">💰 Tarification</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 border rounded-lg bg-blue-50">
                          <Label className="text-blue-700 font-medium">Prix Neuf (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.price_new}
                            onChange={(e) => setFormData({ ...formData, price_new: e.target.value })}
                            className="mt-2"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="p-4 border rounded-lg bg-orange-50">
                          <Label className="text-orange-700 font-medium">Prix Occasion (€)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.price_used}
                            onChange={(e) => setFormData({ ...formData, price_used: e.target.value })}
                            className="mt-2"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="p-4 border rounded-lg bg-[#635BFF]/5">
                          <Label className="text-[#5248d9] font-medium">Prix Location (€/jour)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.price_rental}
                            onChange={(e) => setFormData({ ...formData, price_rental: e.target.value })}
                            className="mt-2"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg">
                      <Checkbox
                        checked={formData.show_price}
                        onCheckedChange={(checked) => setFormData({ ...formData, show_price: checked })}
                      />
                      <Label className="cursor-pointer">
                        👁️ Afficher le prix sur la fiche produit
                      </Label>
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">🏷️ Type d'offre</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-2">
                        {[
                          { value: 'neuf', label: 'Neuf', color: 'bg-blue-100 text-blue-700 border-blue-300' },
                          { value: 'occasion', label: 'Occasion', color: 'bg-orange-100 text-orange-700 border-orange-300' },
                          { value: 'location', label: 'Location', color: 'bg-purple-100 text-purple-700 border-purple-300' },
                          { value: 'service', label: 'Service', color: 'bg-green-100 text-green-700 border-green-300' },
                          { value: 'travaux', label: 'Travaux', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
                          { value: 'etude', label: 'Étude', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
                          { value: 'talent', label: 'Talent', color: 'bg-pink-100 text-pink-700 border-pink-300' },
                          { value: 'presentation', label: 'Présentation', color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
                        ].map((type) => (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, listing_type: type.value })}
                            className={`p-3 rounded-lg border-2 font-medium transition-all ${
                              formData.listing_type === type.value
                                ? `${type.color} border-current ring-2 ring-offset-1`
                                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">📦 Stock</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 border rounded-lg">
                          <Label>Stock Neuf</Label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.stock_new}
                            onChange={(e) => setFormData({ ...formData, stock_new: e.target.value })}
                            className="mt-2"
                          />
                        </div>
                        <div className="p-4 border rounded-lg">
                          <Label>Stock Occasion</Label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.stock_used}
                            onChange={(e) => setFormData({ ...formData, stock_used: e.target.value })}
                            className="mt-2"
                          />
                        </div>
                        <div className="p-4 border rounded-lg">
                          <Label>Stock Location</Label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.stock_rental}
                            onChange={(e) => setFormData({ ...formData, stock_rental: e.target.value })}
                            className="mt-2"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Statut de disponibilité</Label>
                        <Select value={formData.availability_status} onValueChange={(value) => setFormData({ ...formData, availability_status: value })}>
                          <SelectTrigger className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="available">✅ Disponible</SelectItem>
                            <SelectItem value="on_order">📋 Sur commande</SelectItem>
                            <SelectItem value="out_of_stock">❌ Rupture de stock</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Délai de livraison</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <Select value={formData.delivery_time_type} onValueChange={(value) => setFormData({ ...formData, delivery_time_type: value })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">Prédéfini</SelectItem>
                              <SelectItem value="on_demand">À déterminer</SelectItem>
                            </SelectContent>
                          </Select>
                          {formData.delivery_time_type === 'fixed' ? (
                            <Select value={formData.delivery_time} onValueChange={(value) => setFormData({ ...formData, delivery_time: value })}>
                              <SelectTrigger>
                                <SelectValue placeholder="Délai" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="24h">24h</SelectItem>
                                <SelectItem value="48h">48h</SelectItem>
                                <SelectItem value="72h">72h</SelectItem>
                                <SelectItem value="7j">7 jours</SelectItem>
                                <SelectItem value="15j">15 jours</SelectItem>
                                <SelectItem value="1m">1 mois</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              placeholder="Ex: 2-3 semaines"
                              value={formData.delivery_time}
                              onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB: SPECS (Dynamic Template Fields) */}
                {activeTab === 'specs' && (
                  <div className="space-y-4">
                    {loadingTemplate ? (
                      <div className="p-8 text-center">
                        <FaSpinner className="animate-spin text-2xl text-[#635BFF] mx-auto mb-2" />
                        <p className="text-gray-500">Chargement du template...</p>
                      </div>
                    ) : !formData.category_id ? (
                      <div className="p-8 text-center bg-yellow-50 rounded-lg border border-yellow-200">
                        <FaClipboardList className="text-4xl text-yellow-500 mx-auto mb-3" />
                        <p className="font-medium text-yellow-800">
                          Sélectionnez d'abord une catégorie
                        </p>
                        <p className="text-sm text-yellow-600 mt-1">
                          La fiche technique sera adaptée à la catégorie choisie
                        </p>
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => setActiveTab('general')}
                        >
                          ← Retour aux infos générales
                        </Button>
                      </div>
                    ) : !currentTemplate ? (
                      <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                        <Box className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="font-medium text-gray-700">
                          Pas de fiche technique spécifique
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Cette catégorie utilise le formulaire standard. Utilisez l'onglet "Détails" pour les spécifications.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Template Info */}
                        <div className="p-4 bg-[#635BFF]/5 rounded-lg border border-[#635BFF]/20">
                          <div className="flex items-center gap-3">
                            <FaClipboardList className="text-[#635BFF]" />
                            <div>
                              <p className="font-medium text-[#0A2540]">
                                {i18n.language === 'fr' ? currentTemplate.name_fr : currentTemplate.name_en}
                              </p>
                              <p className="text-sm text-gray-500">
                                {currentTemplate.sections?.length || 0} sections • {currentTemplate.fields?.length || 0} champs
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Dynamic Sections & Fields */}
                        {(currentTemplate.sections || [])
                          .sort((a, b) => (a.position || 0) - (b.position || 0))
                          .map((section) => {
                            const SectionIcon = SECTION_ICONS[section.icon_lucide] || Box;
                            const sectionFields = (currentTemplate.fields || [])
                              .filter(f => f.section_id === section.id && f.visible !== false)
                              .sort((a, b) => (a.position || 0) - (b.position || 0));
                            
                            if (sectionFields.length === 0) return null;
                            
                            return (
                              <div key={section.id} className="border rounded-lg overflow-hidden">
                                {/* Section Header */}
                                <button
                                  type="button"
                                  onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
                                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-[#635BFF]/10 rounded-lg flex items-center justify-center">
                                      <SectionIcon size={16} className="text-[#635BFF]" />
                                    </div>
                                    <span className="font-medium text-[#0A2540]">
                                      {i18n.language === 'fr' ? section.name_fr : section.name_en}
                                    </span>
                                    <span className="text-xs text-gray-500">({sectionFields.length})</span>
                                  </div>
                                  {expandedSections[section.id] ? (
                                    <ChevronDown size={20} className="text-gray-400" />
                                  ) : (
                                    <ChevronRight size={20} className="text-gray-400" />
                                  )}
                                </button>
                                
                                {/* Section Fields */}
                                {expandedSections[section.id] && (
                                  <div className="p-4 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {sectionFields.map((field) => (
                                        <DynamicField
                                          key={field.id}
                                          field={field}
                                          value={formData.template_fields[field.name]}
                                          onChange={(value) => setFormData(prev => ({
                                            ...prev,
                                            template_fields: {
                                              ...prev.template_fields,
                                              [field.name]: value
                                            }
                                          }))}
                                          language={i18n.language}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}

                {/* TAB: DETAILS */}
                {activeTab === 'details' && (
                  <div className="space-y-4">
                    <div>
                      <Label>État / Condition</Label>
                      <textarea
                        className="w-full min-h-[80px] p-3 border rounded-md resize-y mt-2"
                        value={formData.condition_details}
                        onChange={(e) => setFormData({ ...formData, condition_details: e.target.value })}
                        placeholder="Ex: Excellent état, révisé en 2023, moins de 500h d'utilisation..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Poids</Label>
                        <Input
                          value={formData.weight}
                          onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                          placeholder="ex: 15 tonnes, 250 kg..."
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Dimensions</Label>
                        <Input
                          value={formData.dimensions}
                          onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                          placeholder="ex: 12m x 3m x 4m (LxlxH)"
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Spécifications techniques</Label>
                      <textarea
                        className="w-full min-h-[120px] p-3 border rounded-md resize-y mt-2 font-mono text-sm"
                        value={formData.technical_specs}
                        onChange={(e) => setFormData({ ...formData, technical_specs: e.target.value })}
                        placeholder="• Puissance: 350 kW&#10;• Profondeur max: 60m&#10;• Couple: 200 kNm&#10;• Diamètre: jusqu'à 2000mm"
                      />
                      <p className="text-xs text-gray-500 mt-1">Une spécification par ligne, utilisez • pour les puces</p>
                    </div>

                    <div>
                      <Label>Garantie</Label>
                      <Input
                        value={formData.warranty}
                        onChange={(e) => setFormData({ ...formData, warranty: e.target.value })}
                        placeholder="ex: 2 ans, 12 mois pièces et main d'œuvre..."
                        className="mt-2"
                      />
                    </div>
                  </div>
                )}

                {/* TAB: IMAGES */}
                {activeTab === 'images' && (
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#0A2540] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                      />
                      <label htmlFor="image-upload" className="cursor-pointer">
                        <div className="text-4xl mb-2">📷</div>
                        <p className="font-medium text-gray-700">Cliquez pour ajouter des images</p>
                        <p className="text-sm text-gray-500 mt-1">ou glissez-déposez vos fichiers ici</p>
                      </label>
                      {uploadingImages && (
                        <div className="mt-3">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0A2540] mx-auto"></div>
                          <p className="text-sm text-gray-500 mt-1">Upload en cours...</p>
                        </div>
                      )}
                    </div>

                    {formData.images.length > 0 && (
                      <div>
                        <Label className="mb-2 block">Images ajoutées ({formData.images.length})</Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {formData.images.map((url, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={url.startsWith('http') ? url : `${API}${url}`}
                                alt={`Product ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg border"
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Error'; }}
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ×
                              </button>
                              {index === 0 && (
                                <span className="absolute bottom-1 left-1 bg-[#0A2540] text-white text-xs px-2 py-0.5 rounded">Principal</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <Label>Ou ajoutez des URLs d'images</Label>
                      <textarea
                        className="w-full min-h-[80px] p-3 border rounded-md text-sm mt-2"
                        placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                        value={formData.images.filter(url => url.startsWith('http')).join('\n')}
                        onChange={(e) => {
                          const urls = e.target.value.split('\n').map(url => url.trim()).filter(Boolean);
                          const uploadedImages = formData.images.filter(url => !url.startsWith('http'));
                          setFormData({ ...formData, images: [...uploadedImages, ...urls] });
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Navigation & Submit Buttons */}
                <div className="flex justify-between items-center pt-4 border-t mt-6">
                  <div className="flex gap-2">
                    {activeTab !== 'general' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const tabs = ['general', 'pricing', 'details', 'images'];
                          const currentIndex = tabs.indexOf(activeTab);
                          setActiveTab(tabs[currentIndex - 1]);
                        }}
                      >
                        ← Précédent
                      </Button>
                    )}
                    {activeTab !== 'images' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const tabs = ['general', 'pricing', 'details', 'images'];
                          const currentIndex = tabs.indexOf(activeTab);
                          setActiveTab(tabs[currentIndex + 1]);
                        }}
                      >
                        Suivant →
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                    <Button type="submit" className="bg-[#0A2540]">Enregistrer</Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-center font-medium w-16">Pos.</th>
                    <th className="p-2 text-left font-medium">Produit</th>
                    <th className="p-2 text-left font-medium">Proprio</th>
                    <th className="p-2 text-left font-medium">Prix</th>
                    <th className="p-2 text-center font-medium">Statut</th>
                    <th className="p-2 text-center font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => (
                    <tr key={product.id} className="border-t hover:bg-gray-50" data-testid={`product-row-${index}`}>
                      <td className="p-2 text-center">
                        <input
                          type="number"
                          min="1"
                          value={product.position || 999}
                          onChange={(e) => handlePositionChange(product.id, parseInt(e.target.value) || 999)}
                          className="w-12 h-7 text-center text-xs border rounded focus:outline-none focus:ring-1 focus:ring-[#635BFF]"
                        />
                      </td>
                      <td className="p-2">
                        <div className="font-medium text-sm">{(i18n.language === 'fr' ? product.name_fr : product.name_en)?.substring(0, 25)}{(i18n.language === 'fr' ? product.name_fr : product.name_en)?.length > 25 ? '...' : ''}</div>
                        {product.featured && <span className="text-[10px] bg-[#635BFF] text-white px-1 rounded">★</span>}
                      </td>
                      <td className="p-2">
                        <div className="text-xs">{getOwnerName(product)?.substring(0, 12)}</div>
                        <button onClick={() => openOwnerDialog(product)} className="text-[10px] text-[#635BFF] hover:underline">
                          Changer
                        </button>
                      </td>
                      <td className="p-2 text-xs">
                        {product.price_new && <div>{product.price_new}€</div>}
                        {product.price_rental && <div className="text-gray-500">{product.price_rental}€/j</div>}
                        {!product.show_price && <div className="text-[#635BFF]">Sur demande</div>}
                      </td>
                      <td className="p-2 text-center">
                        {product.status === 'active' && <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Actif</span>}
                        {product.status === 'pending' && <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">En attente</span>}
                        {product.status === 'inactive' && <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">Inactif</span>}
                        {product.status === 'rejected' && <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">Rejeté</span>}
                      </td>
                      <td className="p-2">
                        <div className="flex justify-center gap-1">
                          {/* Toggle Status */}
                          {(product.status === 'active' || product.status === 'approved') ? (
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-orange-500" onClick={() => handleToggleStatus(product.id, product.status)} disabled={togglingId === product.id}>
                              {togglingId === product.id ? <FaSpinner className="animate-spin text-xs" /> : <FaPause className="text-xs" />}
                            </Button>
                          ) : product.status === 'inactive' ? (
                            <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-green-500" onClick={() => handleToggleStatus(product.id, product.status)} disabled={togglingId === product.id}>
                              {togglingId === product.id ? <FaSpinner className="animate-spin text-xs" /> : <FaPlay className="text-xs" />}
                            </Button>
                          ) : null}
                          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => handleEdit(product)} data-testid={`edit-product-${index}`}>
                            <FaEdit className="text-xs" />
                          </Button>
                          <Button size="sm" variant="destructive" className="h-7 w-7 p-0" onClick={() => handleDelete(product.id)} data-testid={`delete-product-${index}`}>
                            <FaTrash className="text-xs" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Change Owner Dialog */}
        <Dialog open={ownerDialogOpen} onOpenChange={setOwnerDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FaExchangeAlt className="text-[#635BFF]" />
                Changer le propriétaire
              </DialogTitle>
            </DialogHeader>
            
            {selectedProduct && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium">{selectedProduct.name_fr}</p>
                  <p className="text-sm text-gray-500">Propriétaire actuel: {getOwnerName(selectedProduct)}</p>
                </div>

                <div>
                  <Label>Nouveau propriétaire</Label>
                  <Select value={newOwnerId} onValueChange={setNewOwnerId}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Sélectionner un utilisateur" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.filter(u => u.role !== 'admin').map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setOwnerDialogOpen(false)}>Annuler</Button>
                  <Button onClick={handleChangeOwner} className="bg-[#635BFF]">Confirmer</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
    </AdminLayout>
  );
}
